import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useUserSettings } from "@services/UserSettings";
import { Settings } from "@/globals";
import { VehicleCondition } from ".";
import { VehicleUsage } from "./VehicleUsage";
import { type IVehicleDto, type IVehicle, type IVehicleLocationDto, type IVehicleCondition, MarketingPreference } from ".";

export class Vehicle implements IVehicle
{

    vehicleId: string;
    dealNumber?: string;
    nickname?: string;
    rating?: number;
    make?: string;
    model?: string;
    year?: number;
    rego?: string;
    regoState?: string;
    odometer?: number;
    dateLastSetOdometer?: Dayjs;
    vehicleUsage: VehicleUsage;
    estimatedAnnualKilometers: number;
    thumbnail?: string;
    condition: IVehicleCondition;
    location: IVehicleLocationDto;
    marketingPreference: MarketingPreference;
    isInPma: boolean;
    isShared: boolean;
    isCarePlanEnabled: boolean;

    constructor( model: IVehicleDto )
    {
        this.vehicleId = model.vehicleId;
        this.dealNumber = model.dealNumber;
        this.nickname = model.nickname;
        this.rating = model.rating;
        this.make = model.make;
        this.model = model.model;
        this.year = model.year;
        this.rego = model.rego;
        this.regoState = model.regoState;
        this.odometer = Math.floor( model.odometer ?? 0 );
        this.dateLastSetOdometer = model.dateLastSetOdometer == null ? undefined : dayjs( model.dateLastSetOdometer );
        this.isInPma = model.isInPma == true;
        this.isShared = model.isShared;
        this.isCarePlanEnabled = model.isCarePlanEnabled;
        this.vehicleUsage = model.vehicleUsage ?? VehicleUsage.Unknown;
        this.estimatedAnnualKilometers = model.estimatedAnnualKilometers ?? VehicleUsage.toKilometers( this.vehicleUsage ) ?? Settings.EstimatedAnnualKilometers;
        this.thumbnail = model.thumbnail;
        this.location = model.location;
        this.marketingPreference = model.marketingPreference;
        this.condition = new VehicleCondition( this, model.condition );
    }

    /**
     * True if all the required vehicle properties have been set.  If False, the user
     * is not able to enter MyGarage until they are set in the EditVehicleProperties,
     * via a route guard on the MyGarage page's route
     */
    public get hasRequiredProperties()
    {
        if( ( this.odometer ?? 0 ) == 0 ) return false; // Odometer hasnt been set
        //if( this.vehicleUsage == VehicleUsage.Unknown ) return false; // Vehicle Usage hasnt been set
        return true;
    }

    /**
     * Gets the nickname or the displayName if the nickname has no value
     */
    public get name()
    {
        return this.nickname ?? this.displayName;
    }

    /**
     * Gets the display name of the vehicle
     */
    public get displayName()
    {
        if( this.make == null || this.model == null ) return '';
        return `${ this.make ?? '' } ${ this.model ?? '' }`.trim();
    }

    /**
     * True if the vehicle has a valid display name.  Will be false if there is no make and model
     */
    public get hasDisplayName()
    {
        return this.displayName.length > 0;
    }

    /**
     * Gets the display name of the vehicle and its nickname
     */
    public get displayNameAndNickname()
    {
        if( this.nickname == null ) return this.displayName;
        return `${ this.displayName } (${ this.nickname })`;
    }

    /**
     * True if the odometer needs to be updated
     */
    public get isOdometerUpdateRequired()
    {
        return this.dateLastSetOdometer == undefined 
            || dayjs().diff( this.dateLastSetOdometer, 'days' ) >= ( 7 * 6 ); // every 6 weeks
    }
    
    /**
     * Gets the vehicle's list item props for rendering in a select list
     * @returns
     */
    public getListItemProps()
    {

        // Use the nickname if we have one with key vehicle attributes
        if( this.nickname ) return {
            title: this.nickname,
            subtitle: this.displayName
        };

        // Use the year if we have one, plus the key attributes
        if( this.year ) return {
            title: this.year,
            subtitle: `${this.make} ${this.model}`
        };

        // Otherwise make a list item out of just the make and model attributes
        return {
            title: this.make,
            subtitle: this.model
        };

    }

    /**
     * True if the consumer can receive marketing messages
     */
    get allowMarketingMessages()
    {
        return ( this.marketingPreference & MarketingPreference.AllowMarketing ) === MarketingPreference.AllowMarketing;
    }

    /**
     * True if the consumer can receive update messages, alerts, notifications from the dealership
     */
    get allowUpdateMessages()
    {
        return ( this.marketingPreference & MarketingPreference.AllowUpdates ) === MarketingPreference.AllowUpdates;
    }

    /**
     * True if the consumer can receive either updates or marketing messages
     */
    get allowMessages()
    {
        return this.marketingPreference > 0;
    }

};
